import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu, Button, Tag } from 'antd';
import {
  RightOutlined,
  LeftOutlined,
  HomeOutlined,
  HistoryOutlined,
  LinkOutlined,
  // BranchesOutlined,
  BarChartOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { ReactComponent as Logo } from '@assets/logo_cerne.svg';
import { ReactComponent as LogoCollapsed } from '@assets/logo_symbol_barevny_inverzni.svg';

import { UserDropdown, OrganizationProjectSelect } from '@components';
import GlobalSearch, {
  GLOBAL_SEARCH_OPEN_EVENT,
} from './GlobalSearch/GlobalSearch';

import { ROUTES } from '@config';

import { FLAG_KEYS } from '@featureFlags/flagKeys';
import { useFeatureFlag } from '@featureFlags/useFeatureFlag';
import { useRedesign } from '@featureFlags/useRedesign';

import { useActiveProject, useUser } from '@hooks';

import { ReactComponent as LogoRedesign } from '@assets/redesign/logo_cerne.svg';
import { ReactComponent as LogoCollapsedRedesign } from '@assets/redesign/logo_symbol_barevny.svg';

import styles from './PageLayout.module.scss';

const { Sider } = Layout;

type PageLayoutProps = {
  children: React.ReactNode;
};

const PageLayout = ({ children }: PageLayoutProps) => {
  const { _ } = useLingui();
  const [collapsed, setCollapsed] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const activeProject = useActiveProject();
  const user = useUser();
  const showBetaBadge = useFeatureFlag(FLAG_KEYS.DEMO_BANNER);
  const redesign = useRedesign();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((open) => !open);
      }
    };
    const onOpen = () => setSearchOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(GLOBAL_SEARCH_OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(GLOBAL_SEARCH_OPEN_EVENT, onOpen);
    };
  }, []);

  const ActiveLogo = redesign ? LogoRedesign : Logo;
  const ActiveLogoCollapsed = redesign ? LogoCollapsedRedesign : LogoCollapsed;

  return (
    <Layout className={styles.rootLayout}>
      <Layout hasSider>
        <Sider
          className={styles.sider}
          collapsible
          collapsedWidth={50}
          collapsed={collapsed}
          trigger={null}
          theme="light"
          width={224}
        >
          <div className={styles.sidebarHeader}>
            <div className={styles.logoArea}>
              {collapsed ? (
                <ActiveLogoCollapsed className={styles.logoCollapsed} />
              ) : (
                <ActiveLogo className={styles.logo} />
              )}
              {!collapsed && showBetaBadge && (
                <Tag color="blue" className={styles.betaTag}>
                  Beta
                </Tag>
              )}
            </div>
            {!collapsed && (
              <div className={styles.sidebarSelects}>
                <OrganizationProjectSelect />
              </div>
            )}
          </div>
          <div className={styles.collapseButtonContainer}>
            <Button
              size="small"
              shape="circle"
              icon={
                collapsed ? (
                  <RightOutlined className={styles.uncollapseIcon} />
                ) : (
                  <LeftOutlined className={styles.collapseIcon} />
                )
              }
              onClick={() => setCollapsed(!collapsed)}
              aria-label={collapsed ? _(msg`Expand sidebar`) : _(msg`Collapse sidebar`)}
              aria-expanded={!collapsed}
              data-testid={collapsed ? 'collapse-button-open' : 'collapse-button-close'}
            />
          </div>

          {/* Menu - always visible, icons only when collapsed */}
          {(activeProject || user.is_superuser) && (
            <Menu
              selectedKeys={[location.pathname]}
              inlineCollapsed={collapsed}
              items={[
                ...(activeProject
                  ? [
                      {
                        key: ROUTES.FLOWS,
                        icon: <HomeOutlined />,
                        label: (
                          <span className={styles.menuLabel}>
                            <Trans>Flows</Trans>
                          </span>
                        ),
                        onClick: () => navigate(ROUTES.FLOWS),
                      },
                      {
                        key: ROUTES.EXECUTIONS,
                        icon: <HistoryOutlined />,
                        label: (
                          <span className={styles.menuLabel}>
                            <Trans>Executions</Trans>
                          </span>
                        ),
                        onClick: () => navigate(ROUTES.EXECUTIONS),
                      },
                      {
                        key: ROUTES.EXECUTIONS_ALL,
                        icon: <ExclamationCircleOutlined />,
                        label: (
                          <span className={styles.menuLabel}>
                            <Trans>Failing executions</Trans>
                          </span>
                        ),
                        onClick: () => navigate(ROUTES.EXECUTIONS_ALL),
                      },
                      {
                        key: ROUTES.CONNECTIONS,
                        icon: <LinkOutlined />,
                        label: (
                          <span className={styles.menuLabel}>
                            <Trans>Connections</Trans>
                          </span>
                        ),
                        onClick: () => navigate(ROUTES.CONNECTIONS),
                      },
                      {
                        key: ROUTES.ANALYTICS,
                        icon: <BarChartOutlined />,
                        label: (
                          <span className={styles.menuLabel}>
                            <Trans>Statistics</Trans>
                          </span>
                        ),
                        onClick: () => navigate(ROUTES.ANALYTICS),
                      },
                    ]
                  : []),
                ...(user.is_superuser
                  ? [
                      {
                        key: ROUTES.BILLING,
                        icon: <DollarOutlined />,
                        label: (
                          <span className={styles.menuLabel}>
                            <Trans>Billing</Trans>
                          </span>
                        ),
                        onClick: () => navigate(ROUTES.BILLING),
                      },
                    ]
                  : []),
              ]}
            />
          )}
          <div className={styles.sidebarBottom}>
            <UserDropdown collapsed={collapsed} />
          </div>
        </Sider>
        <Layout>
          <div className={styles.content}>{children}</div>
        </Layout>
      </Layout>
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </Layout>
  );
};

export default PageLayout;
